import React, { useEffect, useState } from 'react'
import { Modal, Checkbox, Button, List, Spin, Alert } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  fetchLinkableVenues,
  linkVenuesToXero,
} from 'src/features/XeroIntegrationSlice'
import { H4 } from 'src/branding/typography'

const LinkVenuesModal = ({ visible, onClose, venueId }) => {
  const dispatch = useDispatch()
  const { linkableVenues, linkableVenuesLoading, linkingVenues } = useSelector(
    state => state.xeroIntegration,
  )
  const username = useSelector(
    state => state.auth?.account?.username || state.auth?.account?.emailAddress,
  )
  const [selectedVenueIds, setSelectedVenueIds] = useState([])
  const [organizationName, setOrganizationName] = useState('')

  useEffect(() => {
    if (visible && venueId && username) {
      // Fetch linkable venues when modal opens
      dispatch(fetchLinkableVenues(venueId, username))
        .then(data => {
          if (data?.currentOrganization?.name) {
            setOrganizationName(data.currentOrganization.name)
          }

          // Pre-select already linked venues (they will be disabled)
          const alreadyLinked =
            data?.venues?.filter(v => v.isLinked).map(v => v._id) || []
          setSelectedVenueIds(alreadyLinked)
          return data
        })
        .catch(error => {
          console.error('Error fetching linkable venues:', error)
        })
    }
  }, [visible, venueId, username, dispatch])

  const handleVenueToggle = (venueId, isLinked) => {
    if (isLinked) return // Cannot toggle already linked venues

    setSelectedVenueIds(prev => {
      if (prev.includes(venueId)) {
        return prev.filter(id => id !== venueId)
      } else {
        return [...prev, venueId]
      }
    })
  }

  const handleLinkVenues = async () => {
    // Only link venues that are not already linked
    const venuesToLink = selectedVenueIds.filter(id => {
      const venue = linkableVenues.find(v => v._id === id)
      return venue && !venue.isLinked
    })

    if (venuesToLink.length === 0) {
      onClose()
      return
    }

    const result = await dispatch(linkVenuesToXero(venueId, venuesToLink))
    if (result) {
      onClose()
      // Optionally refresh venue data
    }
  }

  const availableVenues = linkableVenues.filter(v => !v.hasConflict)
  const conflictingVenues = linkableVenues.filter(v => v.hasConflict)

  return (
    <Modal
      title={`Link Other Venues to Xero`}
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key='link'
          type='primary'
          loading={linkingVenues}
          onClick={handleLinkVenues}
          disabled={selectedVenueIds.length === 0}
        >
          Link Selected Venues
        </Button>,
      ]}
      width={600}
    >
      <ModalContent>
        {organizationName && (
          <H4 style={{ marginBottom: 16 }}>
            Xero Organization: <strong>{organizationName}</strong>
          </H4>
        )}

        <p style={{ marginBottom: 16 }}>
          Select which venues should use this existing Xero connection. Venues
          that are already linked are shown as checked and cannot be unlinked
          from this modal.
        </p>

        {linkableVenuesLoading ? (
          <SpinContainer>
            <Spin size='large' />
          </SpinContainer>
        ) : (
          <>
            {availableVenues.length === 0 && conflictingVenues.length === 0 ? (
              <Alert
                message='No other venues available'
                description='There are no other venues under your account to link.'
                type='info'
              />
            ) : (
              <>
                <List
                  dataSource={availableVenues}
                  renderItem={venue => (
                    <VenueItem>
                      <Checkbox
                        checked={selectedVenueIds.includes(venue._id)}
                        disabled={venue.isLinked}
                        onChange={() =>
                          handleVenueToggle(venue._id, venue.isLinked)
                        }
                      >
                        <VenueName>
                          {venue.title}
                          {venue.isLinked && (
                            <LinkedBadge>Already Linked</LinkedBadge>
                          )}
                        </VenueName>
                      </Checkbox>
                    </VenueItem>
                  )}
                />

                {conflictingVenues.length > 0 && (
                  <ConflictSection>
                    <Alert
                      message='Cannot Link These Venues'
                      description='The following venues are already connected to a different Xero organization and cannot be linked:'
                      type='warning'
                      style={{ marginBottom: 12 }}
                    />
                    <List
                      dataSource={conflictingVenues}
                      renderItem={venue => (
                        <ConflictVenueItem>
                          <Checkbox disabled checked={false}>
                            <VenueName style={{ opacity: 0.5 }}>
                              {venue.title}
                            </VenueName>
                          </Checkbox>
                        </ConflictVenueItem>
                      )}
                    />
                  </ConflictSection>
                )}
              </>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default LinkVenuesModal

const ModalContent = styled.div`
  min-height: 200px;
`

const SpinContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`

const VenueItem = styled(List.Item)`
  border: none !important;
  padding: 12px 16px !important;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  .ant-checkbox-wrapper {
    width: 100%;
  }
`

const ConflictVenueItem = styled(List.Item)`
  border: none !important;
  padding: 8px 16px !important;
  background-color: #fff7e6;
  margin-bottom: 4px;

  .ant-checkbox-wrapper {
    width: 100%;
  }
`

const VenueName = styled.span`
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`

const LinkedBadge = styled.span`
  font-size: 12px;
  font-weight: normal;
  color: #52c41a;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  padding: 2px 8px;
  border-radius: 4px;
`

const ConflictSection = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e8e8e8;
`
